/**
 * Common Date Filter Utilities for CRM Portal
 *
 * Implements local-calendar calculations (preventing unsafe UTC shifts)
 * for predefined ranges:
 * - Today
 * - Last 7 Days
 * - Last 30 Days
 * - This Quarter
 * - YTD
 * - Custom Range
 */

export const PRESETS = {
  ALL: "all",
  TODAY: "today",
  LAST_7_DAYS: "last_7_days",
  LAST_30_DAYS: "last_30_days",
  THIS_MONTH: "this_month",
  THIS_QUARTER: "this_quarter",
  YTD: "ytd",
  CUSTOM: "custom",
};

export const PRESET_LABELS = {
  [PRESETS.ALL]: "All Time",
  [PRESETS.TODAY]: "Today",
  [PRESETS.LAST_7_DAYS]: "Last 7 Days",
  [PRESETS.LAST_30_DAYS]: "Last 30 Days",
  [PRESETS.THIS_MONTH]: "This Month",
  [PRESETS.THIS_QUARTER]: "This Quarter",
  [PRESETS.YTD]: "YTD",
  [PRESETS.CUSTOM]: "Custom Range",
};

/**
 * Format a Date object as YYYY-MM-DD using local calendar date values.
 * Strictly avoids UTC conversion to prevent timezone date shifting.
 */
export const formatLocalDate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD string into a local Date object.
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  const date = new Date(year, month, day);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Calculate preset start_date and end_date based on a reference date (defaulting to today).
 */
export const calculatePresetRange = (preset, refDate = new Date()) => {
  const today = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());

  switch (preset) {
    case PRESETS.TODAY: {
      const todayStr = formatLocalDate(today);
      return {
        start_date: todayStr,
        end_date: todayStr,
        preset: PRESETS.TODAY,
      };
    }

    case PRESETS.LAST_7_DAYS: {
      // Inclusive 7 calendar days: today - 6 days through today
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(today),
        preset: PRESETS.LAST_7_DAYS,
      };
    }

    case PRESETS.LAST_30_DAYS: {
      // Inclusive 30 calendar days: today - 29 days through today
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(today),
        preset: PRESETS.LAST_30_DAYS,
      };
    }

    case PRESETS.THIS_MONTH: {
      // First day of current calendar month through today
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(today),
        preset: PRESETS.THIS_MONTH,
      };
    }

    case PRESETS.THIS_QUARTER: {
      // First day of current calendar quarter through today
      const currentMonth = today.getMonth(); // 0-11
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      const start = new Date(today.getFullYear(), quarterStartMonth, 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(today),
        preset: PRESETS.THIS_QUARTER,
      };
    }

    case PRESETS.YTD: {
      // January 1 of current year through today
      const start = new Date(today.getFullYear(), 0, 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(today),
        preset: PRESETS.YTD,
      };
    }

    case PRESETS.ALL:
    default:
      return {
        start_date: "",
        end_date: "",
        preset: PRESETS.ALL,
      };
  }
};

/**
 * Validate start_date and end_date strings (YYYY-MM-DD).
 */
export const validateDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return { isValid: true, error: "" };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (startDate && !dateRegex.test(startDate)) {
    return { isValid: false, error: "Invalid start date format (expected YYYY-MM-DD)" };
  }
  if (endDate && !dateRegex.test(endDate)) {
    return { isValid: false, error: "Invalid end date format (expected YYYY-MM-DD)" };
  }

  if (startDate && endDate && startDate > endDate) {
    return { isValid: false, error: "Start date cannot be after end date" };
  }

  return { isValid: true, error: "" };
};

/**
 * Attach or remove start_date and end_date on an API params object.
 */
export const buildDateFilterParams = (dateFilter, existingParams = {}) => {
  const params = { ...existingParams };
  if (dateFilter?.start_date) {
    params.start_date = dateFilter.start_date;
  } else {
    delete params.start_date;
  }

  if (dateFilter?.end_date) {
    params.end_date = dateFilter.end_date;
  } else {
    delete params.end_date;
  }

  return params;
};
